import {parseJSON} from '../../Common/json';
import {api} from '../../Common/fetchWithTimeout';
import Base64 from '../../GraphEditor/Base64';
import {uploadFile} from './file';

/**
 * Copyright (c) 2006-2020, JGraph Ltd
 * Copyright (c) 2006-2020, Gaudenz Alder
 *
 * Usage: DiagramEditor.editElement(elt) where elt is an img or object with
 * a data URI src or data attribute or an svg node with a content attribute.
 *
 * See https://jgraph.github.io/drawio-integration/javascript.html
 */
export const DiagramEditor = function (view, initialized) {
    this.view = view;
    this.initialized = initialized;

    const self = this;

    this.handleMessageEvent = function (evt) {
        if (self.frame != null && evt.source === self.frame.contentWindow &&
            evt.data.length > 0) {
            const msg = parseJSON(evt.data);

            if (msg != null) {
                self.handleMessage(msg);
            }
        }
    };
}

DiagramEditor.editElement = function (elt, view) {
    if (!elt.diagramEditorStarting) {
        elt.diagramEditorStarting = true;

        return new DiagramEditor(view, function () {
            delete elt.diagramEditorStarting;
        }).editElement(elt);
    }
};

DiagramEditor.prototype.drawDomain = 'https://embed.diagrams.net/';

DiagramEditor.prototype.xml = null;

DiagramEditor.prototype.format = 'xmlsvg';

DiagramEditor.prototype.frameStyle = 'position:absolute;border:0;width:100vw;height:100vh;top:0;right:0;bottom:0;left:0;border:0;';

DiagramEditor.prototype.editElement = function (elem) {
    const src = this.getElementData(elem);
    this.startElement = elem;

    api(src, {
        method: 'GET'
    }).then(content => this.startEditing(content, 'xmlsvg'));

    return this;
};

DiagramEditor.prototype.getElementData = function (elem) {
    return elem.getAttribute('src');
};

DiagramEditor.prototype.setElementData = function (elem, data) {
    elem.setAttribute('src', data);
    return elem;
};

DiagramEditor.prototype.startEditing = function (data, format, title) {
    if (this.frame == null) {
        window.addEventListener('message', this.handleMessageEvent);
        this.format = (format != null) ? format : this.format;
        this.title = (title != null) ? title : this.title;
        this.data = data;
        this.frame = this.createFrame(
            this.getFrameUrl(),
            this.getFrameStyle());

        this.diagramModal = document.createElement('div');
        this.diagramModal.className = 'ui overlay fullscreen modal front scrolling transition hidden';

        document.body.appendChild(this.diagramModal);

        $(this.diagramModal).modal('show');

        this.setWaiting(true);
        this.diagramModal.append(this.frame);
    }
};

DiagramEditor.prototype.setWaiting = function (waiting) {
    if (this.startElement != null) {
        let elt = this.startElement;
        if (elt != null) {
            if (waiting) {
                this.frame.style.pointerEvents = 'none';
                this.previousCursor = elt.style.cursor;
                elt.style.cursor = 'wait';
            } else {
                elt.style.cursor = this.previousCursor;
                this.frame.style.pointerEvents = '';
            }
        }
    }
};

DiagramEditor.prototype.setActive = function (active) {
    if (active) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = this.previousOverflow;
    }
};

DiagramEditor.prototype.stopEditing = function () {
    if (this.frame != null) {
        window.removeEventListener('message', this.handleMessageEvent);
        $(this.diagramModal).modal('hide');
        $(this.diagramModal).modal('destroy');
        this.diagramModal.remove();
        this.setActive(false);
        this.frame = null;
    }
};

DiagramEditor.prototype.postMessage = function (msg) {
    if (this.frame != null) {
        this.frame.contentWindow.postMessage(JSON.stringify(msg), '*');
    }
};

DiagramEditor.prototype.getData = function () {
    return this.data;
};

DiagramEditor.prototype.getTitle = function () {
    return this.title;
};

DiagramEditor.prototype.getFrameStyle = function () {
    return this.frameStyle;
};

DiagramEditor.prototype.getFrameUrl = function () {
    return `${this.drawDomain}?proto=json&spin=1&ui=min&splash=0&libraries=1&configure=1&noSaveBtn=1`;
};

DiagramEditor.prototype.createFrame = function (url, style) {
    const frame = document.createElement('iframe');
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('style', style);
    frame.setAttribute('src', url);

    return frame;
};

DiagramEditor.prototype.setStatus = function (messageKey, modified) {
    this.postMessage(
        {action: 'status', messageKey: messageKey, modified: modified});
};

DiagramEditor.prototype.handleMessage = function (msg) {
    if (msg.event === 'configure') {
        this.configureEditor();
    } else if (msg.event === 'init') {
        this.initializeEditor();
    } else if (msg.event === 'autosave') {
        this.save(msg.xml, true, this.startElement);
    } else if (msg.event === 'export') {
        this.export(msg.data, this.startElement);
        this.setElementData(this.startElement, msg.data);
        this.stopEditing();
        this.xml = null;
    } else if (msg.event === 'save') {
        this.save(msg.xml, false, this.startElement);
        this.xml = msg.xml;

        if (msg.exit) {
            msg.event = 'exit';
        } else {
            this.setStatus('allChangesSaved', false);
        }
    }

    if (msg.event === 'exit') {
        if (this.format !== 'xml') {
            if (this.xml != null) {
                this.postMessage({
                    action: 'export', format: this.format,
                    xml: this.xml, spinKey: 'export',
                });
            } else {
                this.stopEditing(msg);
            }
        } else {
            if (msg.modified == null || msg.modified) {
                this.save(msg.xml, false, this.startElement);
            }

            this.stopEditing(msg);
        }
    }
};

DiagramEditor.prototype.configureEditor = function () {
    this.postMessage({
        action: 'configure',
        config: {}
    });
};

DiagramEditor.prototype.initializeEditor = function () {
    this.postMessage({
        action: 'load',
        autosave: '0',
        saveAndExit: '1',
        modified: 'unsavedChanges',
        xml: this.getData(),
        title: this.getTitle(),
    });
    this.setWaiting(false);
    this.setActive(true);
    this.initialized();
};

DiagramEditor.prototype.save = function (data, draft, elt) {
    this.done(data, draft, elt);
};

DiagramEditor.prototype.export = function (data, elt) {
    const svgEncoded = data.replace('data:image/svg+xml;base64,', '');
    const svg = ((window.atob) ? atob(svgEncoded) : Base64.decode(svgEncoded));
    const pathname = new URL(elt.src).pathname;
    const fileUploadUrl = pathname.substring(0, pathname.lastIndexOf("/"));

    this.view.dispatch(this.view.state.tr.deleteSelection());
    const file = new File(
        [svg],
        'draw.io diagram',
        {
            type: 'image/svg+xml',
        });
    uploadFile(fileUploadUrl, file, this.view, true);
};

DiagramEditor.prototype.done = function () {
};

DiagramEditor.prototype.initialized = function () {
};